import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import ImageManagementModalForm from "../../components/CommonPage/components/ImageManagementModalForm";
import Button from "../../components/Button";
import Loader from "../../components/Loader";
import useAlert from "../../hooks/useAlert";

import { getPageDataAPI } from "../../api/page";
import { getDynamicRecordAPI, updateDynamicRecordAPI } from "../../api/pagecrud";

import styles from "./imageEditorPage.module.css";

const buildFormData = (formData, modelName, primaryKey, id) => {
  const payload = new FormData();
  Object.keys(formData || {}).forEach((key) => {
    const value = formData[key];
    if (value instanceof File) {
      payload.append(key, value);
    } else if (Array.isArray(value) || (value && typeof value === "object")) {
      payload.append(`data[${key}]`, JSON.stringify(value));
    } else {
      payload.append(`data[${key}]`, value ?? "");
    }
  });
  payload.append("model_name", modelName);
  payload.append("primaryKey", primaryKey);
  payload.append("id", id);
  return payload;
};

const ImageEditorPage = () => {
  const { pageCode, id, role } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const defaultsRef = useRef(null);

  const normalizedPageCode = String(pageCode || "").toLowerCase();
  const isImageManagement = normalizedPageCode === "image_management";

  const formFields = useMemo(() => config?.form || [], [config]);
  const primaryKey = config?.primaryKey || "id";

  const fetchEditorData = async () => {
    if (!pageCode || !id) return;
    setLoading(true);
    try {
      const pageResponse = await getPageDataAPI(pageCode);
      const pageKey = Object.keys(pageResponse)[0];
      const pageData = pageResponse[pageKey];
      setConfig({ ...pageData, code: pageKey });

      const record = await getDynamicRecordAPI(
        pageData.model_name,
        id,
        pageData.primaryKey || "id",
      );

      setFormData(record || {});
      defaultsRef.current = { ...(record || {}) };
    } catch (error) {
      showAlert("error", error.response?.data?.message || "Failed to load record");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isImageManagement) return;
    fetchEditorData();
  }, [pageCode, id]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validateForm = () => {
    const nextErrors = {};
    formFields.forEach((field) => {
      if (!field.required) return;
      const value = formData[field.name];
      const hasFileValue =
        field.type === "file" &&
        value &&
        (typeof value === "string" ? value.trim() !== "" : Boolean(value?.name));
      const missingValue =
        value === undefined ||
        value === null ||
        value === "" ||
        (field.type === "file" && !hasFileValue);
      if (missingValue) {
        nextErrors[field.name] = `${field.label} is required`;
      }
    });
    return nextErrors;
  };

  const handleSave = async () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) {
      showAlert("error", Object.values(validationErrors)[0]);
      return;
    }

    try {
      setLoading(true);
      const payload = buildFormData(
        formData,
        config.model_name,
        primaryKey,
        formData[primaryKey],
      );
      await updateDynamicRecordAPI(config.model_name, formData[primaryKey], payload, primaryKey);
      showAlert("success", "Image updated successfully");
      defaultsRef.current = { ...formData };
    } catch (error) {
      showAlert("error", error.response?.data?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (!defaultsRef.current) return;
    setFormData({ ...defaultsRef.current });
  };

  if (!isImageManagement) {
    navigate(`/${role}/page/${pageCode}`);
    return null;
  }

  if (loading && !config) {
    return (
      <div className={styles.pageWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.pageWrap}>
      <div className={styles.headerRow}>
        <div>
          <h2 className={styles.title}>Image Editor</h2>
          <p className={styles.subtitle}>Edit background color or image</p>
        </div>
        <Button variant="secondary" onClick={() => navigate(`/${role}/page/${pageCode}`)}>
          <i className="bi bi-arrow-left" /> Back
        </Button>
      </div>

      <div className={styles.editorCard}>
        <ImageManagementModalForm
          formFields={formFields}
          formData={formData}
          errors={errors}
          readOnly={false}
          getFieldLabel={(field) => (
            <>
              {field.label} {field.required && <span style={{ color: "red" }}>*</span>}
            </>
          )}
          handleChange={handleChange}
          onReset={handleReset}
          onSave={handleSave}
        />
      </div>
    </div>
  );
};

export default ImageEditorPage;
